// c:\Users\Seshu\Downloads\Genmech\Genmech\client\src\utils\offlineDb.js
import Dexie from "dexie";

export const db = new Dexie("GenmechOfflineDB");

// Define IndexedDB tables and indices
// In Dexie, we specify primary key and indexed properties (keys we want to query by)
db.version(1).stores({
  customers: "++_id, name, phone, groupId, localId, syncStatus, isDeleted",
  dealers: "++_id, companyName, name, dealerId, localId, syncStatus, isDeleted",
  customerGroups: "++_id, groupName, localId, syncStatus, isDeleted",
  projectExpenses: "++_id, projectName, customerId, localId, syncStatus, isDeleted",
  vouchers: "++_id, voucherNo, engineerLookup, localId, syncStatus, isDeleted",
  travels: "++_id, engineerLookup, localId, syncStatus, isDeleted",
  complaints: "++_id, complaintId, customerId, localId, syncStatus, isDeleted",
  visits: "++_id, visitId, complaintId, localId, syncStatus, isDeleted",
  dailyUpdates: "++_id, engineerName, localId, syncStatus, isDeleted",
  accountingNotes: "++_id, noteNumber, localId, syncStatus, isDeleted",
  generalServices: "++_id, clientName, localId, syncStatus, isDeleted",
  deliveryChallans: "++_id, challanNo, localId, syncStatus, isDeleted",
  earthPits: "++_id, reportNo, localId, syncStatus, isDeleted",
  materialStocks: "++_id, itemName, itemCode, productId, documentGroupKey, entryType, transactionType, localId, syncStatus, isDeleted",
  inventories: "++_id, productId, itemCode, productName, localId, syncStatus, isDeleted",
  quotations: "++_id, quotationNo, customerId, localId, syncStatus, isDeleted",
  storageLocations: "++_id, name, area, localId, syncStatus, isDeleted",
  storageAreas: "++_id, name, localId, syncStatus, isDeleted",
  
  // Audit tables
  stockMovements: "++movementId, materialId, transactionId, syncStatus",
  syncQueue: "++id, url, method, localId, timestamp",
  syncLogs: "++id, timestamp, action, status"
});

/**
 * Generates a valid MongoDB ObjectID 24-character hex string.
 * This ensures compatibility with Mongoose ObjectID validation on the backend.
 */
export const generateObjectId = () => {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16).padStart(8, "0");
  const machineId = "8492ac"; // Identifier for client app
  const processId = Math.floor(Math.random() * 65535).toString(16).padStart(4, "0");
  const counter = Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0");
  return timestamp + machineId + processId + counter;
};

/**
 * Local stock deduction/addition processor for offline transactions.
 * Prevents double deduction by maintaining transaction history.
 */
export const logOfflineStockMovement = async (stockEntry) => {
  if (!stockEntry) return;

  const {
    _id: transactionId,
    productId,
    itemCode,
    itemName,
    quantity,
    transactionType, // 'in', 'out', 'none'
    entryType // 'purchase', 'sale', 'order'
  } = stockEntry;

  const qty = Number(quantity) || 0;
  if (qty <= 0 || transactionType === "none" || entryType === "order") {
    return; // Quotations/orders do not affect physical stock counts
  }

  // Determine movement type for log
  const movementType = transactionType === "in" ? "IN" : "OUT";
  const movementQty = transactionType === "in" ? qty : -qty;

  try {
    // 1. Add record to local stockMovements log
    await db.stockMovements.add({
      movementId: generateObjectId(),
      materialId: productId || itemCode || itemName,
      transactionType: entryType,
      transactionId: transactionId,
      quantity: qty,
      movementType: movementType,
      createdAt: new Date().toISOString(),
      syncStatus: "pending_create"
    });

    // 2. Adjust inventories current stock count
    let inventoryRecord = null;
    if (productId) {
      inventoryRecord = await db.inventories.where("productId").equals(productId).first();
    }
    if (!inventoryRecord && itemCode) {
      inventoryRecord = await db.inventories.where("itemCode").equals(itemCode).first();
    }
    if (!inventoryRecord && itemName) {
      inventoryRecord = await db.inventories.where("productName").equals(itemName).first();
    }

    if (inventoryRecord) {
      const currentStock = Number(inventoryRecord.currentStock) || 0;
      await db.inventories.update(inventoryRecord._id, {
        currentStock: Number((currentStock + movementQty).toFixed(2)),
        syncStatus: "pending_update",
        lastUpdated: new Date().toISOString()
      });
    } else {
      // Create new inventory item dynamically if it doesn't exist
      const generatedProductId = productId || generateObjectId();
      await db.inventories.add({
        _id: generateObjectId(),
        productId: generatedProductId,
        itemCode: itemCode || `MAT-${Date.now()}`,
        productName: itemName || "Unknown Material",
        description: itemName || "",
        currentStock: movementQty,
        openingStock: 0,
        syncStatus: "pending_create",
        lastUpdated: new Date().toISOString()
      });
    }
  } catch (err) {
    console.error("Failed to process offline stock calculation:", err);
  }
};
